// DesignConfig から、Claude Code / Claude Design へ貼るためのプロンプトを生成する。
// 参考実装として現在のプレビューHTMLをコードブロックで同梱する(画像は data URL を除いて軽くする)。

import { FONT_OPTIONS, FONT_STACKS, type DesignConfig, type FontKind } from "./design";
import { readableText } from "./color";
import { buildPreviewHtml } from "./previewTemplate";
import { TEMPLATES } from "./templates";
import { resolveValues } from "./templates/shared";

export type PromptTarget = "code" | "design";

// 二重引用符文字列なのでバッククォートをそのまま含められる(コードフェンス用)。
const FENCE = "```";

const TONE: Record<FontKind, string> = {
  gothic: "モダンで明快、可読性を重視した印象に仕上げてください。",
  mincho: "上品で落ち着いた、信頼感と伝統を感じる印象に仕上げてください。",
};

function fontLabel(font: string): string {
  return FONT_OPTIONS.find((o) => o.value === font)?.label ?? font;
}

// プロンプト用に、画像スロットの data URL を空にした config を返す(base64 の混入を防ぐ)。
function stripImages(c: DesignConfig): DesignConfig {
  const def = TEMPLATES[c.template];
  const values = { ...c.values };
  for (const f of def.fields) if (f.kind === "image") values[f.key] = "";
  return { template: c.template, values };
}

export function buildPrompt(c: DesignConfig, target: PromptTarget): string {
  const def = TEMPLATES[c.template];
  const v = resolveValues(def, c.values);
  const html = buildPreviewHtml(stripImages(c));

  const brand = v.brandColor ?? "";
  const accent = v.accentColor ?? "";
  const base = v.baseColor ?? "";
  const font = (v.font as FontKind) ?? "gothic";
  const onBrand = readableText(brand || "#000000");
  const onAccent = readableText(accent || "#000000");
  const onBase = readableText(base || "#ffffff");
  const label = fontLabel(font);
  const stack = FONT_STACKS[font] ?? "";
  const headline = v.headline ?? "";
  const subhead = v.subhead ?? "";
  const ctaLabel = v.ctaLabel ?? "";

  if (target === "design") {
    return [
      "# ランディングページ デザイン依頼",
      "",
      "ランディングページのデザインを作成してください。",
      "",
      `- テンプレート: **${def.label}**`,
      "",
      "## ブランドの方向性",
      "",
      `- ブランドカラー **${brand}** を主役に据える。`,
      `- アクセントカラー **${accent}** は CTA・強調にのみ使う。`,
      `- ベースは **${base}** を背景にした、余白広めのレイアウト。`,
      `- タイポグラフィは **${label}**。`,
      "",
      "## トーン & マナー",
      "",
      TONE[font],
      "",
      "## 必要なセクション",
      "",
      "Header / Hero / Features(3 項目)/ CTA バンド / Footer",
      "",
      "## コピー",
      "",
      `- 見出し: ${headline}`,
      `- サブ見出し: ${subhead}`,
      `- CTA: ${ctaLabel}`,
      "",
      "## 現在のプレビュー(たたき台)",
      "",
      `${FENCE}html`,
      html,
      FENCE,
      "",
    ].join("\n");
  }

  return [
    "# ランディングページ実装依頼",
    "",
    "以下のデザイン仕様に沿って、ランディングページを実装してください。",
    "",
    `- テンプレート: **${def.label}**`,
    "",
    "## デザイントークン",
    "",
    "| 役割 | 色 | 文字色 | 用途 |",
    "| --- | --- | --- | --- |",
    `| brand | ${brand} | ${onBrand} | ヘッダ背景・見出し・主要ボタン |`,
    `| accent | ${accent} | ${onAccent} | CTA・強調 |`,
    `| base | ${base} | ${onBase} | ページ背景・本文 |`,
    "",
    `- フォント: **${label}**`,
    `- font-family: ${stack}`,
    "",
    "## 文言",
    "",
    `- 見出し: ${headline}`,
    `- サブ見出し: ${subhead}`,
    `- CTA ボタン: ${ctaLabel}`,
    "",
    "## レイアウト(上から)",
    "",
    "1. Header — ロゴ + ナビゲーション",
    "2. Hero — 見出し・サブ見出し・CTA ボタン",
    "3. Features — 3 カラムの特徴カード",
    "4. CTA バンド — brand 背景に CTA を再掲",
    "5. Footer — コピーライト",
    "",
    "## 実装メモ",
    "",
    "- 色は CSS 変数(`--brand` など)で定義し、brand / accent 上のテキストは上表の「文字色」でコントラストを確保する。",
    "- 720px 以下で Features を 1 カラムに落とすレスポンシブ対応。",
    "- 日本語フォントは Noto Sans JP / Noto Serif JP を Google Fonts から読み込み、上記 font-family を用いる(ウェイト 400–900 を正確に反映。未ロード時はシステムフォントへフォールバック)。",
    "",
    "## 参考実装",
    "",
    "この HTML の構造・配色をそのまま土台にして構いません。",
    "",
    `${FENCE}html`,
    html,
    FENCE,
    "",
  ].join("\n");
}
